import * as Api from '@/request/api/defaultApi';
import { initSDK360, isSDK360Ready, isSDK360Inited } from '@/utils/SDK360';

/**
 * 360 联运 SDK 初始化（软件启动时必须调用，见文档 3.1.1 / QAQ 第 3 条）。
 *
 * 抽成共享模块的原因：init 依赖 get360Config 这个网络请求，请求失败时 SDK 的 appid/qid 为空，
 * 之后所有接口都会返回 errno 110001「请先调用init方法进行初始化！」，支付彻底不可用且只能重启软件。
 * 抽出来后，下单前可以按需补一次 init 自愈（见 useSDK360Payment.start）。
 */

// 进行中的初始化 promise，用于并发去重：启动时与下单前可能同时触发，
// 共用同一个 promise 避免重复请求 get360Config / 重复 init。
let initPromise: Promise<boolean> | null = null;

/** 真正执行一次初始化流程 */
async function doInit(): Promise<boolean> {
    if (!isSDK360Ready()) {
        console.warn('[SDK360] SDK360.js 未加载，跳过初始化');
        return false;
    }

    // 从云端获取 appid / qid（文档要求不可硬编码）
    let configRes: any;
    try {
        configRes = await Api.get360Config();
    } catch (err) {
        // 网络异常 / 接口非 2xx：http.ts 会 reject 响应体，这里必须兜住，否则冒泡成 Uncaught (in promise)
        console.error('[SDK360] 获取配置请求失败，跳过初始化', err);
        return false;
    }
    if (configRes?.code != 200 || !configRes?.data) {
        console.error('[SDK360] 获取配置失败，跳过初始化', configRes);
        return false;
    }
    const appid = configRes.data.app_id; // 注意：接口字段为 app_id
    const qid = configRes.data.qid;

    // mid / m2 由 C++ 读取 360 注册表后回传（文档 3.1，取不到传空串，非 null）。
    // 64位优先 HKLM\SOFTWARE\WOW6432Node\360Safe\LiveUp[mid|m2]，32位优先 HKLM\SOFTWARE\360Safe\LiveUp；
    // 依次回退 HKCU\SOFTWARE\360SoftMgr\OEM、HKCU\SOFTWARE\SoftMgrOEM\OEM。
    // 注：官方 demo 只传 appid/qid，SDK 内部 mid/m2 默认空串也能正常下单，
    // 故 mid/m2 与 errno 10017「不支持的接入模式」无关，正式包仍应由 C++ 补齐用于 360 侧归因。
    let mid = '';
    let m2 = '';
    try {
        const appStore = useAppStore();
        const envRes: any = await appStore.MyWin.WorkAiApi('get_360_env', '');
        mid = envRes?.mid ?? envRes?.data?.mid ?? '';
        m2 = envRes?.m2 ?? envRes?.data?.m2 ?? '';
    } catch (err) {
        console.warn('[SDK360] 获取 mid/m2 失败（C++ get_360_env 未就绪？），按空串处理', err);
    }

    initSDK360({ appid, qid, mid, m2 });

    // 以 SDK 自身的 checkIsInit 口径确认结果，而不是假定 init 一定成功
    const ok = isSDK360Inited();
    if (!ok) console.error('[SDK360] init 调用完成但 appid/qid 仍为空');
    return ok;
}

/** * 确保 SDK 已初始化。已初始化则直接返回；并发调用共用同一个进行中的 promise。
 * 失败不抛异常，只返回 false —— 调用方自行决定降级提示。
 *
 * @param force 为 true 时忽略「已初始化」判断，强制重新初始化（一般不需要）
 * @returns 初始化是否成功
 */
export async function ensureSDK360Inited(force = false): Promise<boolean> {
    if (!force && isSDK360Inited()) return true;
    // 已有初始化在飞行中，复用它，避免并发重复请求
    if (initPromise) return initPromise;

    initPromise = doInit().finally(() => {
        // 释放守卫，允许下次失败后重试（成功时上面的 isSDK360Inited 会短路，不会重复执行）
        initPromise = null;
    });
    return initPromise;
}
